import React, { useCallback, useEffect, useState } from 'react';

import AdminStatusCard from '../../components/admin/AdminStatusCard';
import ApiCreditMonitoringPanel from '../../components/admin/ApiCreditMonitoringPanel';
import DeploymentSafetyPanel from '../../components/admin/DeploymentSafetyPanel';
import { Button, PageHeader } from '@copilot/ui';
import { EmptyState } from '@copilot/ui';
import Loader from '../../components/common/Loader';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Category label -> where in the response its provider info lives.
// Metadata shown is deliberately limited to provider name + model -
// never configured. is the only credential-adjacent signal ever
// rendered (see AdminStatusCard), per the "no secrets" rule.
const PROVIDER_SECTIONS = [
  { key: 'stt', title: 'STT Provider' },
  { key: 'tts', title: 'TTS Provider' },
  { key: 'translation', title: 'Translation Provider' },
];

function AdminSystemPage() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const result = await adminApi.getAdminSystemStatus();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load system status.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Silent re-read: no loading/error state, last good data kept on failure.
  const refreshSystemStatus = useCallback(async ({ isStale }) => {
    const result = await adminApi.getAdminSystemStatus();
    if (!isStale()) setData(result);
  }, []);

  useAutoRefresh(refreshSystemStatus);

  return (
    <div>
      <PageHeader
        title="System"
        description="Backend, database, and provider configuration status."
      />
      <DeploymentSafetyPanel />

      {isLoading && <Loader label="Loading system status..." />}

      {!isLoading && error && (
        <EmptyState
          title="Unable to load system status"
          description={error}
          action={
            <Button onClick={() => setReloadToken((token) => token + 1)}>
              Retry
            </Button>
          }
        />
      )}

      {!isLoading && !error && data && (
        <>
          <div className="stat-cards admin-status-cards">
            <AdminStatusCard
              title="Backend"
              statusLabel={capitalize(data.backend.status)}
              isPositive={data.backend.status === 'healthy'}
            />
            <AdminStatusCard
              title="Database"
              statusLabel={capitalize(data.database.status)}
              isPositive={data.database.status === 'connected'}
            />
          </div>

          <div className="admin-card-header-row admin-section-heading">
            <div className="card-title">Provider Configuration</div>
          </div>

          <div className="stat-cards admin-status-cards">
            {PROVIDER_SECTIONS.map(({ key, title }) => {
              const provider = data.providers[key];
              const metadata = [
                { label: 'Provider', value: capitalize(provider.provider) },
              ];
              if (provider.model) {
                metadata.push({ label: 'Model', value: provider.model });
              }

              return (
                <AdminStatusCard
                  key={key}
                  title={title}
                  statusLabel={provider.configured ? 'Configured' : 'Not Configured'}
                  isPositive={provider.configured}
                  metadata={metadata}
                />
              );
            })}
          </div>
        </>
      )}

      {/* Loads on its own, so a failure here never hides the cards above. */}
      <ApiCreditMonitoringPanel />
    </div>
  );
}

export default AdminSystemPage;
